import axios, {AxiosError} from 'axios';

export enum ApiErrorIntent {
    WARNING = 'warning',
    DANGER = 'danger',
}

export type ApiError = {
    /**
     * Status kód resultu, pokud je 0, jedná se o jinou chybu - bez response.
     */
    status?: number;
    /**
     * Původní response error z API.
     */
    responseError?: any | AxiosError;
    /**
     * Při 422 jsou zde form errors.
     */
    formErrors?: any;
    /**
     * Text zprávy/label.
     */
    messageTitle?: string;
    /**
     * Podrobnější popis chyby.
     */
    messageDesc?: string;
    /**
     * Byl request zrušen?
     */
    cancelled: boolean;
    /**
     * Intent, pokud lze určit.
     */
    errorIntent?: ApiErrorIntent;
    /**
     * Seznam dalších doplňujících info.
     */
    detailErrors?: string[];
};

export const standardErrorInterceptor = (error: any | AxiosError): any => {
    const isCancelled = axios.isCancel(error);
    if (isCancelled) {
        console.debug('Request was cancelled', error);

        const result: ApiError = {
            cancelled: true,
            responseError: error,
        };

        return Promise.reject(result);
    }

    const is403 = error.response && error.response.status && error.response.status === 403;
    if (!is403) {
        console.error('Standard response error', error);
    } else {
        console.debug('403 response error', error);
    }

    if (!error.response) {
        const result: ApiError = {
            cancelled: false,
            status: 0,
            responseError: error,
            messageTitle: 'Chyba komunikace se serverem',
            messageDesc: 'Server vrátil prázdnou odpověď',
        };

        return Promise.reject(result);
    }

    switch (error.response.status) {
        case 403: {
            const result: ApiError = {
                cancelled: false,
                status: error.response.status,
                responseError: error,
                // @ts-ignore
                ...parseErrorMessage(error),
            };
            return Promise.reject(result);
        }
        case 401: {
            const result: ApiError = {
                cancelled: false,
                status: error.response.status,
                responseError: error,
                messageTitle: 'Nejste přihlášen nebo nemáte dostatečná oprávnění',
                messageDesc: error.response.data.error,
            };

            return Promise.reject(result);
        }
        case 400: {
            if (error.response.data && error.response.data.errors && Array.isArray(error.response.data.errors)) {
                const responseErrors = getFormError(error.response.data);
                console.error('Chyba zpracování dat na serveru', responseErrors);

                const result: ApiError = {
                    cancelled: false,
                    status: error.response.status,
                    responseError: error,
                    messageTitle: 'Chyba zpracování dat na serveru',
                    messageDesc: JSON.stringify(responseErrors),
                };
                return Promise.reject(result);
            } else {
                const result: ApiError = {
                    cancelled: false,
                    errorIntent: ApiErrorIntent.WARNING,
                    status: error.response.status,
                    responseError: error,
                    ...parseErrorMessage(error),
                };
                return Promise.reject(result);
            }
        }
        case 422: {
            if (error.response.data && error.response.data.errors && Array.isArray(error.response.data.errors)) {
                let formErr = getFormError(error.response.data);

                const result: ApiError = {
                    cancelled: false,
                    status: error.response.status,
                    responseError: error,
                    formErrors: formErr,
                };
                return Promise.reject(result);
            } else {
                const result: ApiError = {
                    cancelled: false,
                    status: error.response.status,
                    responseError: error,
                    errorIntent: ApiErrorIntent.WARNING,
                    ...parseErrorMessage(error),
                };

                return Promise.reject(result);
            }
        }
        case 500:
        default: {
            const result: ApiError = {
                cancelled: false,
                status: error.response.status,
                responseError: error,
                errorIntent: parseIntent(error),
                ...parseErrorMessage(error),
            };

            return Promise.reject(result);
        }
    }
};

export function parseIntent(error: any): ApiErrorIntent {
    if (error.response.data && error.response.data.level) {
        switch (error.response.data.level) {
            case 'DANGER':
                return ApiErrorIntent.DANGER;
            case 'WARNING':
                return ApiErrorIntent.WARNING;
        }
    }
    return ApiErrorIntent.DANGER;
}

export function parseErrorMessage(error: any): {messageTitle?: string; messageDesc?: string; detailErrors?: string[]} {
    if (error.response.data && error.response.data.errorMessages) {
        if (error.response.data.errorMessages.length > 1) {
            return {
                messageTitle: 'Nastaly chyby při zpracování',
                detailErrors: error.response.data.errorMessages,
            };
        } else {
            const ll = error.response.data.errorMessages[0].split('\n');

            return {
                messageTitle: 'Nastaly chyby při zpracování',
                messageDesc: ll[0],
                detailErrors: ll.slice(1),
            };
        }
    } else if (error.response.data && error.response.data.message) {
        return {
            messageTitle: error.response.data.message,
            messageDesc: error.response.data.properties!.systemMessage,
            detailErrors: error.response.data.errorMessages,
        };
    } else {
        return {
            messageTitle: `Nastala neočekávaná chyba (${error.response.status} ${error.response.statusText})`,
        };
    }
}

function getFormError(error: any) {
    const formErrors: any = {};

    error.errors.forEach((e: any) => {
        const field = e.field;

        let obj = formErrors;
        let parts: Array<string> = field.split('.');
        parts.forEach((part: string, index: number) => {
            let last = index === parts.length - 1;

            if (last) {
                obj[part] = e.message || e.defaultMessage;
            } else {
                let n = part.indexOf('[');
                if (n >= 0) {
                    let attr = part.substr(0, n);
                    let index = part.substr(n + 1, part.length - 1 - (n + 1));

                    obj[attr] = obj[attr] || [];
                    obj[attr][index] = {};
                    obj = obj[attr][index];
                } else {
                    obj[part] = obj[part] || {};
                    obj = obj[part];
                }
            }
        });
    });

    return formErrors;
}
